import React, { useState, useEffect } from 'react';
import { login, signup, requestPasswordReset, checkEmailExists } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useDebounce } from '../hooks/useDebounce';

const getPasswordStrength = (pw) => {
  if (!pw) return { label: '', color: '', width: 0 };
  let score = 0;
  if (pw.length >= 6) score++;
  if (pw.length >= 10) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { label: 'Weak', color: '#ef4444', width: 20 };
  if (score === 2) return { label: 'Fair', color: '#f59e0b', width: 40 };
  if (score === 3) return { label: 'Good', color: '#eab308', width: 60 };
  if (score === 4) return { label: 'Strong', color: '#22c55e', width: 80 };
  return { label: 'Very Strong', color: '#10b981', width: 100 };
};

const AuthModal = ({ isOpen, onClose, initialTab = 'login' }) => {
  const [tab, setTab] = useState(initialTab);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [emailExists, setEmailExists] = useState(null);
  const [emailChecking, setEmailChecking] = useState(false);
  const { loginUser } = useAuth();
  const { t } = useLanguage();
  const debouncedEmail = useDebounce(email, 800);

  useEffect(() => {
    if (isOpen) {
      setTab(initialTab);
      setError('');
      setShowReset(false);
      setResetSent(false);
      setEmailExists(null);
    }
  }, [isOpen, initialTab]);

  // Check email exists on signup
  useEffect(() => {
    if (tab === 'signup' && debouncedEmail && debouncedEmail.includes('@') && debouncedEmail.length > 5) {
      setEmailChecking(true);
      checkEmailExists(debouncedEmail).then(d => setEmailExists(d.exists)).catch(() => setEmailExists(null)).finally(() => setEmailChecking(false));
    } else if (tab === 'login' && debouncedEmail && debouncedEmail.includes('@') && debouncedEmail.length > 5) {
      setEmailChecking(true);
      checkEmailExists(debouncedEmail).then(d => setEmailExists(d.exists)).catch(() => setEmailExists(null)).finally(() => setEmailChecking(false));
    } else {
      setEmailExists(null);
    }
  }, [debouncedEmail, tab]);

  if (!isOpen) return null;

  const handleLogin = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      loginUser(data.token, data.user);
      onClose();
      setEmail(''); setPassword(''); setUsername(''); setConfirmPw('');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleSignup = async (e) => {
    e?.preventDefault();
    if (!username || username.length < 3) { setError('Username must be at least 3 characters'); return; }
    if (password !== confirmPw) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (emailExists) { setError('An account with this email already exists'); return; }
    setError('');
    setLoading(true);
    try {
      const data = await signup(email, username, password);
      loginUser(data.token, data.user);
      onClose();
      setEmail(''); setPassword(''); setUsername(''); setConfirmPw('');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleReset = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);
    try {
      await requestPasswordReset(email);
      setResetSent(true);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const pwStrength = getPasswordStrength(password);

  const leftPanel = (
    <div className="auth-left-panel">
      <button className="auth-close-btn" onClick={onClose}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
      <div className="auth-left-content">
        <img src="/logo.png" alt="Fast Boost" className="auth-logo" />
        <h2 className="auth-brand-name">Fast <span>Boost</span></h2>
        <p className="auth-brand-subtitle">Premium Discord Server Boosts</p>
        <div className="auth-features">
          <div className="auth-feature"><div className="auth-feature-icon" style={{background:'rgba(34,197,94,0.15)'}}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2"><path d="M13 10V3L4 14h7v7l9-11h-7z"/></svg></div><div><strong>{t('hero.instantDelivery')}</strong><span>Boosts delivered automatically within seconds</span></div></div>
          <div className="auth-feature"><div className="auth-feature-icon" style={{background:'rgba(59,130,246,0.15)'}}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></div><div><strong>{t('hero.securePayments')}</strong><span>Crypto payments with full order tracking</span></div></div>
          <div className="auth-feature"><div className="auth-feature-icon" style={{background:'rgba(245,158,11,0.15)'}}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg></div><div><strong>Best Prices</strong><span>Cheapest boosts on the market, guaranteed</span></div></div>
        </div>
      </div>
    </div>
  );

  if (showReset) {
    return (
      <div className="modal-overlay auth-overlay" onClick={onClose}>
        <div className="auth-fullscreen animate-drop" onClick={e => e.stopPropagation()}>
          {leftPanel}
          <div className="auth-right-panel">
            <div className="auth-right-content">
              <h2>{t('auth.resetPassword')}</h2>
              <p className="auth-right-subtitle">{t('auth.resetDesc')}</p>
              {error && <div className="auth-error">{error}</div>}
              {resetSent ? (
                <div className="auth-success-msg">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  <p>Check your email for a password reset link.</p>
                  <button className="auth-link-btn" onClick={() => { setShowReset(false); setTab('login'); setResetSent(false); setError(''); }}>{t('auth.backToLogin')}</button>
                </div>
              ) : (
                <div className="auth-form" onKeyDown={e => e.key === 'Enter' && handleReset(e)}>
                  <div className="form-group">
                    <label className="form-label">{t('auth.email')}</label>
                    <input type="email" className="form-input" placeholder="you@email.com" value={email} onChange={e => setEmail(e.target.value)} />
                  </div>
                  <button className="btn btn-primary auth-submit-btn" onClick={handleReset} disabled={loading || !email}>
                    {loading ? <span className="loading-spinner"></span> : t('auth.sendResetLink')}
                  </button>
                  <div className="auth-switch-row">
                    <button className="auth-link-btn" onClick={() => { setShowReset(false); setError(''); }}>{t('auth.backToLogin')}</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay auth-overlay" onClick={onClose}>
      <div className="auth-fullscreen animate-drop" onClick={e => e.stopPropagation()}>
        {leftPanel}
        <div className="auth-right-panel">
          <div className="auth-right-content">
            <h2>{tab === 'login' ? t('auth.welcomeBack') : t('auth.createAccount')}</h2>
            <p className="auth-right-subtitle">{tab === 'login' ? t('auth.welcomeBack') : t('auth.getStarted')}</p>
            {error && <div className="auth-error">{error}</div>}

            {tab === 'login' ? (
              <div className="auth-form" onKeyDown={e => e.key === 'Enter' && handleLogin(e)}>
                <div className="form-group">
                  <label className="form-label">{t('auth.email')}</label>
                  <input type="email" className="form-input" placeholder="you@email.com" value={email} onChange={e => setEmail(e.target.value)} />
                  {emailChecking && <div className="input-message loading"><span className="loading-spinner" style={{width:12,height:12}}/> Checking...</div>}
                  {emailExists === false && email.includes('@') && !emailChecking && <div className="input-message error" style={{fontSize:'0.75rem'}}>✕ No account found with this email</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">{t('auth.password')}</label>
                  <div className="password-input-wrap">
                    <input type={showPassword ? 'text' : 'password'} className="form-input" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} />
                    <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg> : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
                    </button>
                  </div>
                  <button className="auth-forgot-btn" onClick={() => { setShowReset(true); setError(''); }}>{t('auth.forgotPassword')}</button>
                </div>
                <button className="btn btn-primary auth-submit-btn" onClick={handleLogin} disabled={loading || !email || !password}>
                  {loading ? <span className="loading-spinner"></span> : t('auth.logIn')}
                </button>
                <div className="auth-divider"><span>{t('auth.or')}</span></div>
                <div className="auth-switch-row">
                  {t('auth.noAccount')} <button className="auth-link-btn" onClick={() => { setTab('signup'); setError(''); }}>{t('auth.signUp')}</button>
                </div>
              </div>
            ) : (
              <div className="auth-form" onKeyDown={e => e.key === 'Enter' && handleSignup(e)}>
                <div className="form-group">
                  <label className="form-label">{t('auth.email')}</label>
                  <input type="email" className={`form-input ${emailExists === true ? 'error' : ''}`} placeholder="you@email.com" value={email} onChange={e => setEmail(e.target.value)} />
                  {emailChecking && <div className="input-message loading"><span className="loading-spinner" style={{width:12,height:12}}/> Checking...</div>}
                  {emailExists === true && !emailChecking && <div className="input-message error" style={{fontSize:'0.75rem'}}>✕ An account with this email already exists</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">{t('auth.username') || 'Username'}</label>
                  <input type="text" className="form-input" placeholder="username" value={username} onChange={e => setUsername(e.target.value)} autoComplete="username" />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('auth.password')}</label>
                  <div className="password-input-wrap">
                    <input type={showPassword ? 'text' : 'password'} className="form-input" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} />
                    <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg> : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
                    </button>
                  </div>
                  {password && (
                    <div className="password-strength">
                      <div className="password-strength-bar">
                        <div className="password-strength-fill" style={{ width: `${pwStrength.width}%`, background: pwStrength.color }} />
                      </div>
                      <span className="password-strength-label" style={{ color: pwStrength.color }}>{pwStrength.label}</span>
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">{t('auth.confirmPassword')}</label>
                  <input type="password" className="form-input" placeholder="••••••••" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} />
                </div>
                <button className="btn btn-primary auth-submit-btn" onClick={handleSignup} disabled={loading || !email || !username || !password || !confirmPw || emailExists === true}>
                  {loading ? <span className="loading-spinner"></span> : t('auth.createAccount')}
                </button>
                <div className="auth-divider"><span>{t('auth.or')}</span></div>
                <div className="auth-switch-row">
                  {t('auth.alreadyHaveAccount')} <button className="auth-link-btn" onClick={() => { setTab('login'); setError(''); }}>{t('auth.signIn')}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
